// @flow
import {setLanguageKey} from '@userstory/i18n';
import {AUTH_ACTION_TYPE} from 'modules/auth/constants';
import type {
    TActionLogInFail,
    TActionLogInStart,
    TActionLogInSuccess,
    TActionLogOut,
    // TActionSetStatusEducation,
    TSetAuthUserCompany,
    TSetAuthUserData,
} from 'modules/auth/reducers/auth';
import type {
    TIsVerifyRequestBody,
    TAuthData,
    TSignUpRequestBody,
    TSuccessResponse,
    TAuthUser,
    TVerificationRequestBody,
    TAuthUserDataPayload,
    TEmailIsVerifyRequestBody,
    TEmailVerificationRequestBody,
    TAuthUserCompanyPayload,
    TForgotPasswordRequestBody,
} from 'modules/auth/types/user';

// import type {TUserEducationData} from 'modules/user/types';
import type {TResponse, TThunkAction} from 'types';

/**
 * @returns экшен
 */
function logInFail(): TActionLogInFail {
    return {
        type: AUTH_ACTION_TYPE.LOGIN_FAIL,
    };
}

// /**
//  * @param payload данные
//  * @returns экшен
//  */
// function setEducationStatus(payload): TActionSetStatusEducation {
//     return {
//         payload,
//         type: AUTH_ACTION_TYPE.SET_STATUS_EDUCATION,
//     };
// }

/**
 * @returns экшен
 */
function logInStart(): TActionLogInStart {
    return {
        type: AUTH_ACTION_TYPE.LOGIN_START,
    };
}

/**
 * @param payload данные
 * @returns экшен
 */
export function logInSuccess(payload: TAuthUser): TActionLogInSuccess {
    return {
        payload,
        type: AUTH_ACTION_TYPE.LOGIN_SUCCESS,
    };
}

/**
 * ActionCreator "Изменить данные текущего аккаунта"
 * @param payload данные
 * @returns экшен
 */
export function setAuthUserData(payload: TAuthUserDataPayload): TSetAuthUserData {
    return {
        payload,
        type: AUTH_ACTION_TYPE.SET_AUTH_USER_DATA,
    };
}

/**
 * ActionCreator "Изменить имя компании для текущего аккаунта"
 * @param payload данные
 * @returns экшен
 */
export function setAuthUserCompany(payload: TAuthUserCompanyPayload): TSetAuthUserCompany {
    return {
        payload,
        type: AUTH_ACTION_TYPE.SET_AUTH_USER_COMPANY,
    };
}

/**
 * Очищаем данные авторизированного юзера
 * @returns экшен
 */
function logOutSuccess(): TActionLogOut {
    return {
        type: AUTH_ACTION_TYPE.LOGOUT,
    };
}

/**
 * Экшен авторизации пользователя
 * @param login Логин пользователя
 * @returns thunk-экшен
 */
export function logIn({email, password, activeLanguageId}: TAuthData): TThunkAction<TResponse<TAuthUser>> {
    return async (dispatch, getState, {tokenApi, authApi}) => {
        dispatch(logInStart());
        // eslint-disable-next-line sort-keys
        const response = await authApi.signIn({email, password, activeLanguageId});
        if (response.success) {
            const {data} = response;
            const token = data.token;
            const languageUrl: any = Object.values(getState()['@i18n'].languageList.list).find(
                (language) => language && language.id && language.id === data.languageId
            );
            dispatch(setLanguageKey(languageUrl && languageUrl.url));
            tokenApi.setToken(token);
            dispatch(logInSuccess(data));
            return {data, success: true};
        }
        const {errors} = response;
        return {errors, success: false};
    };
}

/**
 * Пытается авторизовать пользователя, если есть токен
 * @return возвращает инфу о пользователе
 */
export function autoLogIn(): TThunkAction<TResponse<TAuthUser>> {
    return async (dispatch, getState, {tokenApi, authApi}) => {
        dispatch(logInStart());
        const token = tokenApi.getToken();
        if (token) {
            const response = await authApi.autoSignIn();
            if (!response.success) {
                dispatch(logInFail());
                return {errors: response.errors, success: false};
            }
            const languageUrl: any = Object.values(getState()['@i18n'].languageList.list).find(
                (language) => language && language.id && language.id === response.data.languageId
            );
            dispatch(setLanguageKey(languageUrl && languageUrl.url));
            dispatch(logInSuccess(response.data));
            return {data: response.data, success: true};
        }
        return {errors: {}, success: false};
    };
}

/**
 * Экшен логаута пользователя
 * @returns thunk-экшен
 */
export function logOut(): TThunkAction<Promise<void>> {
    return async (dispatch, getState, {tokenApi}) => {
        tokenApi.logOut();
        dispatch(logOutSuccess());
        dispatch(logInStart());
    };
}

/**
 * Экшен для регистрации компании
 * @param values значения из формы
 * @returns {function(*, *, {authApi: *}): TResponse<*>} thunk-экшен
 */
export function actionSignUp({...values}: TSignUpRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.signUp<TSuccessResponse>(values);
}

/**
 * Экшен для проверки надобности в прохождении пользователем верификации
 * @param values хэш почты (находится в урле ссылки на верификацию)
 * @returns {function(*, *, {authApi: *}): TResponse<*>} thunk-экшен
 */
export function actionIsVerify({...values}: TIsVerifyRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.isVerify(values);
}

/**
 * Экшен для верификации пользователя
 * @param values значения из формы
 * @returns {function(*, *, {authApi: *}): TResponse<*>} thunk-экшен
 */
export function actionVerification({...values}: TVerificationRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.verification(values);
}

/**
 * Экшен для проверки надобности в верификации почты
 * @param values хэш почты (находится в урле ссылки на верификацию)
 * @returns {function(*, *, {authApi: *}): TResponse<*>} thunk-экшен
 */
export function actionEmailIsVerify({...values}: TEmailIsVerifyRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.emailIsVerify(values);
}

/**
 * Экшен для верификации почты
 * @param values значения из формы
 * @returns {function(*, *, {authApi: *}): TResponse<*>} thunk-экшен
 */
export function actionEmailVerification({...values}: TEmailVerificationRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.emailVerification(values);
}

// /**
//  * Экшен получения информации об обучении пользователя
//  * @param userId ид пользователя
//  * @returns null
//  */
// export function actionGetUserEducation(userId: number): TThunkAction<TResponse<null>> {
//     return async (dispatch, getState, {userApi}) => {
//         const response = await userApi.getEducationData<TUserEducationData>(userId);
//         if (response.success) {
//             dispatch(setEducationStatus(response.data));
//             return {data: null, success: true};
//         }
//         return {errors: {}, success: false};
//     };
// }

/**
 * Экшен "Запросить ссылку на изменение пароля (забыли пароль)"
 * @param values значения из формы
 * @returns thunk-экшен
 */
export function forgotPassword(values: TForgotPasswordRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.forgotPassword(values);
}

/**
 * Экшен "Проверка проходил ли пользователь по ссылке восстановления пароля"
 * @param values хэш почты (находится в урле ссылки)
 * @returns thunk-экшен
 */
export function isRecovery(values: TIsVerifyRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.isRecovery(values);
}

/**
 * Экшен "Восстановление пароля"
 * @param values значения из формы
 * @returns thunk-экшен
 */
export function recoveryPassword(values: TVerificationRequestBody): TThunkAction<TResponse<TSuccessResponse>> {
    return async (dispatch, getState, {authApi}) => authApi.recoveryPassword(values);
}
